import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onCatalogOptionClick"];
import { useCallback, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { H1 } from 'foundations-components/transitional/components/H1';
import { H2 } from 'foundations-components/transitional/components/H2';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Tab, Tabs } from 'foundations-components/transitional/components/Tabs';
import { ChatTextInput } from 'academy-common-lib/features/search/components/ChatTextInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useUserInfo } from '../../accounts/accountsQueries';
import I18n from 'I18n';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useNavPageHandlers } from '../../navigation';
import { ACCatalogFilterButton } from 'academy-navigation-lib/components/ACCatalog/ACCatalogFilterButton';
import { ACCatalogMultiSelect } from 'academy-navigation-lib/components/ACCatalog/ACCatalogMultiSelect';
import { ACSearchInput } from 'academy-navigation-lib/components/ACSearchInput/ACSearchInput';
import useNavigate from '../../../hooks/useNavigate';
import { ACADEMY_ASSISTANT_URL_FRAGMENT } from '../../../constants/Navigation';
import HomePageSection from '../../../components/Layouts/HomePageSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AcademyAssistantHomeSearchPrompt = () => {
  var _userInfo$user;
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const {
    userInfo
  } = useUserInfo();
  const firstName = userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.firstName;
  const navigate = useNavigate();
  const [tab, setTab] = useState('assistant');
  const [assistantInput, setAssistantInput] = useState('');
  const _useNavPageHandlers = useNavPageHandlers({
      appearsIn: 'academy-assistant-home-search-prompt'
    }),
    {
      onCatalogOptionClick
    } = _useNavPageHandlers,
    searchInputHandlers = _objectWithoutPropertiesLoose(_useNavPageHandlers, _excluded);
  const onAssistantTabChange = useCallback(next => {
    setTab(next);
  }, []);
  const submitAssistantPrompt = useCallback(() => {
    const trimmed = assistantInput.trim();
    if (!trimmed) {
      return;
    }
    navigate({
      pathname: ACADEMY_ASSISTANT_URL_FRAGMENT,
      search: '',
      hash: '',
      state: {
        initialPrompt: trimmed
      }
    });
  }, [assistantInput, navigate]);
  const onAssistantChatInputChanged = useCallback(e => {
    setAssistantInput(e.target.value);
  }, []);
  const onAssistantKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitAssistantPrompt();
    }
  }, [submitAssistantPrompt]);
  const Header = isMobile ? H4 : H1;
  const Subheader = isMobile ? H5 : H2;
  const tabsAndDividerInlineSize = isMobile || isTablet ? '100%' : '50%';
  return /*#__PURE__*/_jsx(HomePageSection, {
    padding: "0",
    children: /*#__PURE__*/_jsx(Flex, {
      gap: "400",
      children: /*#__PURE__*/_jsxs(View, {
        flexGrow: 1,
        flexWrap: "wrap",
        children: [/*#__PURE__*/_jsx(Header, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.getStarted.header",
            options: {
              firstName
            }
          })
        }), /*#__PURE__*/_jsx(Subheader, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.getStarted.subHeader"
          })
        }), /*#__PURE__*/_jsxs(View, {
          "data-onboarding-hook": "search-or-browse-topics",
          marginBlockStart: "400",
          children: [/*#__PURE__*/_jsxs(View, {
            inlineSize: tabsAndDividerInlineSize,
            marginBlockEnd: "400",
            maxInlineSize: tabsAndDividerInlineSize,
            children: [/*#__PURE__*/_jsx(Text, {
              as: "span",
              typography: isMobile || isTablet ? 'body-125' : undefined,
              children: /*#__PURE__*/_jsxs(Tabs, {
                bordered: false,
                fill: isMobile,
                inlineSize: "70%",
                maxInlineSize: "100%",
                onSelectedChange: e => {
                  const next = e.target.value;
                  if (next === 'assistant' || next === 'legacy') {
                    onAssistantTabChange(next);
                  }
                },
                selected: tab,
                children: [/*#__PURE__*/_jsx(Tab, {
                  tabId: "assistant",
                  title: I18n.text('learningCenter.getStarted.assistantTab')
                }), /*#__PURE__*/_jsx(Tab, {
                  tabId: "legacy",
                  title: I18n.text('learningCenter.getStarted.searchLegacyTab')
                })]
              })
            }), /*#__PURE__*/_jsx(View, {
              "aria-hidden": true,
              borderBlockStartColor: "border-core-subtle",
              borderBlockStartStyle: "solid",
              borderBlockStartWidth: "100",
              inlineSize: "100%"
            })]
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "400",
            children: /*#__PURE__*/_jsx(View, {
              blockSize: "80px",
              flexDirection: "column",
              inlineSize: tabsAndDividerInlineSize,
              justifyContent: "center",
              maxInlineSize: tabsAndDividerInlineSize,
              minInlineSize: "0",
              children: tab === 'assistant' ? /*#__PURE__*/_jsx(View, {
                inlineSize: "100%",
                children: /*#__PURE__*/_jsx(ChatTextInput, {
                  autoFocus: !isMobile,
                  disabled: false,
                  leadingIcon: null,
                  onChatInputChanged: onAssistantChatInputChanged,
                  onKeyDown: onAssistantKeyDown,
                  onSend: submitAssistantPrompt,
                  placeholder: I18n.text('learningCenter.getStarted.assistantPlaceholder'),
                  value: assistantInput
                })
              }) : /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                flexDirection: isMobile ? 'row-reverse' : 'row',
                gap: "400",
                inlineSize: "100%",
                children: [isMobile ? /*#__PURE__*/_jsx(ACCatalogFilterButton, {
                  onCatalogOptionClick: onCatalogOptionClick
                }) : /*#__PURE__*/_jsx(ACCatalogMultiSelect, {
                  onCatalogOptionClick: onCatalogOptionClick
                }), /*#__PURE__*/_jsx(View, {
                  blockSize: "42px",
                  flexGrow: 1,
                  maxInlineSize: "100%",
                  minInlineSize: "0",
                  position: "relative",
                  zIndex: "3",
                  children: /*#__PURE__*/_jsx(View, {
                    inlineSize: "100%",
                    position: "absolute",
                    children: /*#__PURE__*/_jsx(ACSearchInput, Object.assign({
                      placeholder: I18n.text('learningCenter.getStarted.placeholder'),
                      use: "on-dark"
                    }, searchInputHandlers))
                  })
                })]
              })
            })
          })]
        }), !isMobile ? /*#__PURE__*/_jsx(View, {
          "aria-hidden": true,
          borderBlockStartColor: "border-core-subtle",
          borderBlockStartStyle: "solid",
          borderBlockStartWidth: "100",
          "data-test-id": "separator",
          inlineSize: "100%",
          marginBlockStart: "100"
        }) : null]
      })
    })
  });
};
export default AcademyAssistantHomeSearchPrompt;